import { Video } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ComponentProps } from "react";
import SynchronizedVideoPlayer from "@/components/video/SynchronizedVideoPlayer";
import type { VideoSessionTimeline } from "@/utils/videoSessionTimeline";
import type {
  SessionRecordingSummary,
  VideoFile,
} from "@/types";

type SyncedPlayerProps = ComponentProps<typeof SynchronizedVideoPlayer>;

interface VideoPanelProps {
  sessionId: string;
  videos: VideoFile[];
  recordings: SessionRecordingSummary[];
  currentTime: number;
  onTimeUpdate: (time: number) => void;
  onVideoSelect: (videoId: string) => void;
  initialVideoId?: string;
  requestedVideoId?: string;
  onDurationChange: (videoId: string, duration: number) => void;
  onGetVideoControls: SyncedPlayerProps["onGetVideoControls"];
  volume: number;
  onVolumeChange: (value: number) => void;
  syncOffset: number;
  onSyncOffsetChange: SyncedPlayerProps["onSyncOffsetChange"];
  videoCreatedAt: SyncedPlayerProps["videoCreatedAt"];
  telemetryStartTime: SyncedPlayerProps["telemetryStartTime"];
  telemetryTimestampSource: SyncedPlayerProps["telemetryTimestampSource"];
  timeline: VideoSessionTimeline | null;
  onRefreshRecordingPlayback: (
    recordingId: string,
  ) => Promise<string | null>;
  hasActiveClip: boolean;
}

export default function VideoPanel({
  sessionId,
  videos,
  recordings,
  currentTime,
  onTimeUpdate,
  onVideoSelect,
  initialVideoId,
  requestedVideoId,
  onDurationChange,
  onGetVideoControls,
  volume,
  onVolumeChange,
  syncOffset,
  onSyncOffsetChange,
  videoCreatedAt,
  telemetryStartTime,
  telemetryTimestampSource,
  timeline,
  onRefreshRecordingPlayback,
  hasActiveClip,
}: VideoPanelProps) {
  const { t } = useTranslation(["pages", "common"]);

  return (
    <div className="flex flex-col h-full bg-[#0d0d14]">
      <div className="flex items-center justify-between px-3 py-2 bg-[#111827] border-b border-[#1a1a2e] flex-shrink-0">
        <span className="text-xs font-semibold uppercase tracking-widest text-[#4b5563]">{t("pages:sessions.analytics.videoSection")}</span>
        {videos.length > 0 && (
          <span
            className={`inline-flex items-center gap-1.5 text-xs ${
              timeline && !hasActiveClip
                ? "text-[#facc15]"
                : "text-[#4ade80]"
            }`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                timeline && !hasActiveClip
                  ? "bg-[#facc15] shadow-[0_0_4px_#facc15]"
                  : "bg-[#22c55e] shadow-[0_0_4px_#22c55e]"
              }`}
            />
            {timeline && !hasActiveClip ? t("pages:sessions.analytics.recordingGap") : t("pages:sessions.analytics.synced")}
          </span>
        )}
      </div>
      {videos.length > 0 ? (
        <SynchronizedVideoPlayer
          key={sessionId}
          videos={videos}
          recordings={recordings}
          currentTime={currentTime}
          onTimeUpdate={onTimeUpdate}
          onVideoSelect={onVideoSelect}
          initialVideoId={initialVideoId}
          requestedVideoId={requestedVideoId}
          onDurationChange={onDurationChange}
          onGetVideoControls={onGetVideoControls}
          volume={volume}
          onVolumeChange={onVolumeChange}
          syncOffset={syncOffset}
          onSyncOffsetChange={onSyncOffsetChange}
          videoCreatedAt={videoCreatedAt}
          telemetryStartTime={telemetryStartTime}
          telemetryTimestampSource={telemetryTimestampSource}
          timeline={timeline}
          onRefreshRecordingPlayback={onRefreshRecordingPlayback}
          className="w-full h-full"
        />
      ) : (
        <div className="flex-1 flex items-center justify-center text-[#374151]">
          <div className="text-center">
            <Video className="h-10 w-10 mx-auto mb-3 opacity-20" />
            <p className="text-sm font-medium text-[#4b5563]">{t("pages:sessions.analytics.noVideoAvailable")}</p>
            <p className="text-xs mt-1 text-[#374151]">{t("pages:sessions.analytics.uploadVideosHint")}</p>
          </div>
        </div>
      )}
    </div>
  );
}
